import ProjectSession from "../project/project-session"
import ProjectView from "./project-view"
import ProjectLocalSettings from "../../manager/project/project-local-settings"
import {ViewType} from "../../gd/const/views/view-type"
import {VIEWS_SORT_POSITIONS} from "../../gd/const/views/view-type-config"
import Collection from "../../data-structures/collection"
import {SortDirection} from "../../data-structures/sort"
import projectViews_getProjectDefaultView from "./collection/get-default-view"
import projectView_create from "./actions/create"
import projectView_duplicate, {ProjectViewDuplicateParams} from "./actions/duplicate"
import projectView_delete from "./actions/delete"
import projectViews_shouldProjectViewBeRemoved from "./collection/filter/filter-project-view"
import ProjectViewFactory from "./factory"
import ProjectViewsLookup from "../project/inheritance/project-views-lookup"
import {ProjectViewAccess} from "./@const"
import projectViews_normalize from "./collection/normalize-config"
import {ProjectViewConfig} from "./@types";
import projectView_deleteAll from "./actions/delete-all"
import {ITEM_VIEW_PROJECT_INFO, ItemViewType} from "../../gd/app-manager/item-view"
import ArrayUtils from "../../gd/utils/array"
import ProjectView_Form from "./views/form/form-view"
import {PROJECT_EVERYTHING} from "../../gd/const/views/configs/project/workspace-everything"

// const debug = "ProjectViews"
const debug = false

export default class ProjectViews extends Collection<ProjectView> {

    public project              : ProjectSession

    public currentView!         : ProjectView

    public projectViewsLookup?  : ProjectViewsLookup

    // views this user may not see (dropped on load) - kept so save() can not delete them
    private hiddenViews         : ProjectViewConfig[] = []

    constructor(project:ProjectSession) {
        super([])
        debug && console.log(debug, "constructor", project)

        this.project = project
    }

    // new ProjectLoader(projectId) should be initiated before initByProjectId
    // or any other project settings load function should be called
    // to make sure all necessary projects settings are loaded.
    //
    // There is also a problem to use async here because this method is used in ProjectViewsLookup
    // which is used in toJSON functions which can't be async
    static initByProjectId(projectId:ID):ProjectViews {

        const projectSession : ProjectSession = gd.session.projects.get(projectId)!
        const projectViews = new ProjectViews(projectSession)
        projectViews.resetByProjectId(projectId)

        return projectViews
    }

    resetByProjectId(projectId: ID):void {

        debug && console.log(debug, "resetByProjectId() projectId=", projectId)

        const projectSession : ProjectSession | null = gd.session.projects.get(projectId)

        if (!projectSession) {
            throw Error("#3984723hd no project found")
        }

        this.projectViewsLookup = new ProjectViewsLookup(projectSession.id)
        const viewsConfig : ProjectViewConfig[] = this.projectViewsLookup.get()

        debug && console.log(debug, "    viewsConfig=", viewsConfig)

        // const collection = new ProjectViews(projectSession)

        this.hiddenViews = []

        const models: ProjectView[] = _(viewsConfig)
            .filter((projectViewData: any) => {
                switch (projectViewData.systemType) {
                    case ViewType.PROJECT_REQUEST_FORMS:
                        // skip deleted forms views
                        const form = _.find(this.project.properties.forms, form => form.id === projectViewData.formId)
                        if (!form) {
                            // the form may just not be loaded yet - hide the view but keep it in settings
                            this.hiddenViews.push(projectViewData)
                            return false
                        }
                        break
                }

                return true
            })
            .map((projectViewData: any) => {
                return ProjectViewFactory(projectViewData, this)
            })
            .value()

        this.reset(models)

        projectViews_normalize(this)

        this.filter()
        this.sort()

        if (this.currentView) {
            const view = this.get(this.currentView.id)
            if (view) this.setCurrentView(view)
        }

        debug && console.log(debug, 'updateViews', this.currentView, this)
    }

    async onProjectSettingsChanged(projectId: ID) {
        // esli drugoi project - ignore
        // or if project is view in structure - ignore

        if (this.project.isReport) return

        if (this.project.id !== projectId || this.project.isView) return

        this.resetByProjectId(projectId)

        const projectInfoItemView = gd.itemView.findByContainerId(ItemViewType.ITEM_VIEW_PROJECT_INFO)

        // check if project info view is not opened and
        // if current view is deleted just go to default view
        if (
            !projectInfoItemView
            && !this.get(this.currentView?.id)
        ) {
            const defaultView = this.getDefaultView({
                fallbackViewType: this.currentView?.type === ViewType.PROJECT_EVERYTHING ? ViewType.PROJECT_EVERYTHING : undefined
            })
            defaultView.open()
        }
    }

    getNewId(): ID {
        return gd.utils.generateUIDWithCollisionChecking(_.keys(this.byId))
    }

    getByUrl(url: string): ProjectView {
        const urlParts = url.split('/')

        let view
        if (urlParts.length === 3) { // url has no view id
            view = _.find(this.models, (view)=>view.url.includes(url))
        } else {
            view = _.find(this.models, (view)=>view.url === url)
        }

        if (view) {
            return view
        }

        const fallbackViewType = url.includes(PROJECT_EVERYTHING.url) ? ViewType.PROJECT_EVERYTHING : undefined

        return this.getDefaultView({
            fallbackViewType: fallbackViewType
        })
    }

    getBySystemType(viewType: ViewType): ProjectView[] {
        return _.filter(this.models, (view)=>view.systemType === viewType)
    }

    getByFormId(formId:ID) {
        return _.find(this.models, (view)=>{
            if (view.systemType === ViewType.PROJECT_REQUEST_FORMS) {
                return (view as ProjectView_Form).formId === formId
            }

            return false
        })
    }

    getFirst(): ProjectView {
        return this.models[0]
    }

    getLast(): ProjectView {
        return this.models[this.models.length -1]
    }

    moveLeft(projectView: ProjectView) {
        const orderedIds    = _.map(this.models, model=>model.id)
        const oldIndex      = _.indexOf(orderedIds, projectView.id)
        let newIndex        = oldIndex - 1
        if (newIndex < 0) {
            newIndex = 0
        }

        ArrayUtils.moveInArrayMutate(this.models, oldIndex, newIndex)

        _.map(this.models, (item, key)=>{item.sortPosition = key})

        // this.sort()
        this.save()
    }

    moveRight(projectView: ProjectView) {
        const orderedIds    = _.map(this.models, model=>model.id)
        const oldIndex      = _.indexOf(orderedIds, projectView.id)
        let newIndex        = oldIndex + 1
        if (newIndex > orderedIds.length - 1) {
            newIndex = orderedIds.length - 1
        }

        ArrayUtils.moveInArrayMutate(this.models, oldIndex, newIndex)

        _.map(this.models, (item, key)=>{item.sortPosition = key})

        // this.sort()
        this.save()
    }

    setCurrentView(view: ProjectView) {
        this.currentView = view

        if (this.currentView.showInSubmenu) {
            // save to local settings (Remember view)
            const localSettings = new ProjectLocalSettings(this.project.id)
            localSettings.defaultView = this.currentView.id
            localSettings.save()
        }
    }

    getDefaultView(options?: {
        fallbackViewType?   : ViewType // specifies a view type to use as a fallback, if no default view is determined by other logic or inputs
        checkLocalSettings? : boolean
    }): ProjectView {
        return projectViews_getProjectDefaultView(this, options)
    }

    setDefaultView(view:any) {
        if (this.project.settings) {
            this.project.settings.defaultView = view.id
            this.project.settings.save()
        }
    }

    // special list of views available for customers
    getCustomerViews() {
        const viewsSystemTypes = _(this.models).map(view=>view.systemType).uniq().value()

        // get only views that are present in project settings
        return _(this.models)
            .filter((projectView:any)=>
                projectView.access === ProjectViewAccess.PUBLIC &&      // only public views are allowed to be added
                viewsSystemTypes.includes(projectView.systemType)       // only views that are pinned or subviews of system pinned views are allowed to be added
            )
            .value()
    }

    getFormView(requestFormId: ID) {
        return _.find(this.models, view=>{
            if (view.systemType === ViewType.PROJECT_REQUEST_FORMS) {
                const formView = view as ProjectView_Form
                return formView.formId === requestFormId
            }

            return false
        })
    }

    get isCustom() {
        return this.projectViewsLookup?.isCustom()
    }

    get isInherited() {
        return this.projectViewsLookup?.isInherited()
    }

    // remove all views? as there is no more system views
    // posle etogo po idee on inherite zanbovo ili pustoi budet
    async resetCustomViews() {
        if (this.project.settings) {
            this.project.settings.views = null
            this.project.settings.save()
        }
    }

    // reset views' app settings to default (delete config)
    async resetAppSettings(view:ProjectView) {
        // projectView.appSettings = false
        view.saveAppSettings(null)
        await this.save()

        // we also need to delete local settings for this view (!)
        // we cannot deal with this.currentView because current view can be different from item we reset for
        const projectLocalSettings = new ProjectLocalSettings(this.project.id)
        projectLocalSettings.deleteView(view.id)

        gd.getApp().start()
    }

    async deleteAll() {
        return projectView_deleteAll(this)
    }

    async createView(viewConfig:ProjectViewConfig) {
        return projectView_create(this, viewConfig)
    }

    async duplicate(view:ProjectView, params: ProjectViewDuplicateParams) {
        await projectView_duplicate(this, view, params)
    }

    async delete(view:ProjectView) {
        await projectView_delete(this, view)
    }

    // remove views that are not allowed due to access, modules or other restrictions
    filter() {
        const viewsToRemove = _.filter(this.models, (view:ProjectView)=>projectViews_shouldProjectViewBeRemoved(this, view))

        debug && console.log(debug, "filter viewsToRemove", viewsToRemove)

        // remove call is required to clear this.byId too
        _.map(viewsToRemove, (view: ProjectView)=>{
            // remember the raw config so save() can put it back (see save())
            this.hiddenViews.push(view.config)
            this.remove(view.id)
        })
    }

    sort() {
        this.models = _(this.models)
            .orderBy([
                projectView => !_.isNil(projectView.sortPosition) ? 0 : 1,
                projectView => projectView.sortPosition,
                projectView => _.indexOf(VIEWS_SORT_POSITIONS, projectView.systemType)
            ], [
                SortDirection.ASC,
                SortDirection.ASC,
                SortDirection.ASC
            ])
            .map((model, key)=>{
                model.sortPosition = key
                return model
            })
            .value()
    }

    exportJsonInheritance() {
        const res: any = []

        _.map(this.models, view=>{
            if (view.canBeInherited) {
                res.push(view.toJsonInheritance())
            }
        })

        return res
    }

    setSharedViewId(projectView:ProjectView, id:string) {
        projectView.sharedViewId = id
        this.save()
    }

    // get views available to overwrite
    getSaveAsOptions(): ProjectView[] {
        const viewType = this.currentView!.systemType
        // find matching view systemType and it should not be readOnly
        return _.filter(this.models, view=>viewType === view.systemType && !view.acl.isReadonly)
    }

    saveAs(newAppSettingsConfig:any) {
        debug && console.log(debug, "saveAs", newAppSettingsConfig)

        return new Promise((resolve, reject) => {
            gd.modals.project.projectViewSaveAs((values:any)=>{
                const targetView = this.get(values.selectedId)
                if (!targetView) return

                targetView.saveAppSettings(newAppSettingsConfig)
                // this.save()

                gd.goTo(targetView.url)
                resolve(values.selectedId)
            })
        })
    }

    async save() {
        // Collection.add does not dedupe - never write the same view id twice
        const config = _(this.models)
            .uniqBy('id')
            .map((projectView:ProjectView) => {
                const viewConfig = projectView.toJson()

                // if project view is inherited save it to the config
                if (projectView.isInherited) {
                    viewConfig.isInherited = projectView.isInherited
                }
                return viewConfig
            })
            .value()

        // this.models holds only the views THIS user may see - anything filter() dropped
        // (private views of other users, module-disabled types, form views whose form is
        // not loaded) is missing from it. Append those back or the write deletes them
        // for everyone. Views the user deleted are not in hiddenViews, so delete still works.
        const savedIds  = _.keyBy(config, 'id')
        const hidden    = _.reject(this.hiddenViews, (viewConfig:ProjectViewConfig)=>{
            return !!savedIds[viewConfig.id!]
        })

        debug && console.log(debug, "save", config, "hidden", hidden)

        if (this.project.settings) {
            this.project.settings.views = [...config, ...hidden]
            await this.project.settings.save()
        }

        // gd.bus.triggerEvent(SESSION_PROJECT_SETTINGS_CHANGE, this.project.id)
    }
}
